import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import { ArrowLeft, CheckCircle2, LayoutGrid, Tv, Minimize2 } from "lucide-react";
import PsiMoversBoard from "@/components/PsiMoversBoard";
import SponsorSlide from "@/components/SponsorSlide";
import WatchLiveButton from "@/components/WatchLiveButton";
import ServeBox from "@/components/ServeBox";
import CourtTimer from "@/components/CourtTimer";

const TZ = -new Date().getTimezoneOffset();

function ScoreEntry({ id, m, onSaved }) {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (a === "" || b === "") return toast.error("Enter both scores");
    setBusy(true);
    try {
      await api.post(`/tournaments/${id}/matches/${m.key}/result`, { score_a: Number(a), score_b: Number(b) });
      toast.success(`Court ${m.court} score approved`);
      setA(""); setB("");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save score");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-2 mt-3">
      <input data-testid={`tdcb-score-a-${m.key}`} type="number" min="0" value={a} onChange={(e) => setA(e.target.value)}
        className="w-16 bg-black border border-pb-border text-center text-lg font-black text-white px-1 py-2 focus:border-pb-cyan outline-none" placeholder="0" />
      <span className="text-zinc-600 font-black">—</span>
      <input data-testid={`tdcb-score-b-${m.key}`} type="number" min="0" value={b} onChange={(e) => setB(e.target.value)}
        className="w-16 bg-black border border-pb-border text-center text-lg font-black text-white px-1 py-2 focus:border-pb-cyan outline-none" placeholder="0" />
      <button data-testid={`tdcb-submit-${m.key}`} onClick={submit} disabled={busy}
        className="flex-1 bg-pb-red text-black text-[10px] font-black uppercase tracking-widest px-3 py-2.5 hover:opacity-90 disabled:opacity-40">
        {busy ? "Saving…" : "Approve Score"}
      </button>
    </div>
  );
}

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return now;
}

function TvBoard({ t, live, approved, movers, nameOf, divLabel, onExit }) {
  const now = useClock();
  const cols = live.length <= 2 ? "grid-cols-1 md:grid-cols-2" : live.length <= 4 ? "grid-cols-2" : live.length <= 6 ? "grid-cols-2 xl:grid-cols-3" : "grid-cols-3 xl:grid-cols-4";
  return (
    <div className="fixed inset-0 z-[100] bg-black text-white flex flex-col overflow-hidden select-none" data-testid="tv-board">
      <div className="flex items-center justify-between px-8 py-4 border-b border-pb-border/60">
        <div className="flex items-center gap-4">
          <LayoutGrid className="w-8 h-8 text-pb-cyan" />
          <div>
            <div className="font-display font-black uppercase tracking-widest text-3xl text-pb-cyan leading-none" data-testid="tv-title">{t.name}</div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mt-1">Live Court Board · {t.location || ""}</div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="font-mono font-black text-4xl tabular-nums text-white" data-testid="tv-clock">{now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
          <button data-testid="tv-exit" onClick={onExit} title="Exit TV mode (Esc)" className="text-zinc-600 hover:text-white transition-colors"><Minimize2 className="w-6 h-6" /></button>
        </div>
      </div>
      <div className="flex-1 grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-6 p-6 min-h-0">
        <div className="min-h-0 flex flex-col">
          <div className="text-xs font-black uppercase tracking-[0.35em] text-zinc-500 mb-3">On Court Now · {live.length}</div>
          {live.length === 0 ? (
            <div className="flex-1 border border-pb-border text-zinc-600 text-2xl font-display uppercase tracking-widest flex items-center justify-center" data-testid="tv-no-live">Courts are clear — next round loading</div>
          ) : (
            <div className={`grid ${cols} gap-4 flex-1 min-h-0 auto-rows-fr`}>
              {live.map((m) => (
                <div key={m.key} className="border-2 border-pb-cyan/50 bg-pb-cyan/5 p-5 flex flex-col justify-between min-h-0" data-testid={`tv-court-${m.court}`}>
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-display font-black text-pb-cyan text-5xl leading-none">CT {m.court}</span>
                    <div className="flex flex-col items-end gap-2">
                      <CourtTimer since={m.court_assigned_at} size="lg" testid={`tv-timer-${m.court}`} />
                      <span className="text-xs font-black uppercase tracking-widest text-zinc-500 text-right">{divLabel(m)}</span>
                    </div>
                  </div>
                  <div className={`flex-1 flex flex-col justify-center font-display font-black uppercase leading-tight ${live.length <= 2 ? "text-4xl xl:text-6xl" : live.length <= 4 ? "text-3xl xl:text-4xl" : "text-2xl xl:text-3xl"}`}>
                    <ServeBox serving={m.first_serve === "a"} size="lg" testid={`tv-side-a-${m.court}`} className="flex items-center justify-between gap-3"><span className="truncate">{nameOf(m.side_a)}</span>{m.live_score && <span className="font-mono text-[#A3E635] tabular-nums" data-testid={`tv-live-a-${m.court}`}>{m.live_score.a}</span>}</ServeBox>
                    <div className="text-sm text-zinc-600 tracking-[0.4em] my-3 font-mono flex items-center gap-3">VS{m.live_score && <span className="text-red-400 text-xs tracking-widest flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />LIVE · G{m.live_score.game}</span>}</div>
                    <ServeBox serving={m.first_serve === "b"} size="lg" testid={`tv-side-b-${m.court}`} className="flex items-center justify-between gap-3"><span className="truncate">{nameOf(m.side_b)}</span>{m.live_score && <span className="font-mono text-[#A3E635] tabular-nums" data-testid={`tv-live-b-${m.court}`}>{m.live_score.b}</span>}</ServeBox>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="min-h-0 flex flex-col gap-5">
          <div className="[&_[data-testid=tv-psi-movers]]:p-5 [&_[data-testid=tv-psi-movers]_.text-sm]:text-xl [&_[data-testid=tv-psi-movers]_.text-lg]:text-3xl [&_[data-testid=tv-psi-movers]_.text-xl]:text-3xl">
            <PsiMoversBoard data={movers} live={!movers?.final} compact testid="tv-psi-movers" title="Top 3 PSI Movers" />
          </div>
          <div className="border border-pb-border bg-zinc-950 p-4 flex-1 min-h-0 flex flex-col" data-testid="tv-recent">
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.35em] text-pb-red mb-3"><CheckCircle2 className="w-5 h-5" /> Latest Results</div>
            <div className="flex-1 min-h-0 overflow-hidden divide-y divide-pb-border/40">
              {approved.length === 0 && <div className="text-zinc-600 text-sm py-6 text-center">No results yet.</div>}
              {approved.slice(0, 8).map((m) => (
                <div key={m.key} className="py-2.5 flex items-center gap-3 text-lg font-bold" data-testid={`tv-result-${m.key}`}>
                  <span className={`truncate flex-1 ${m.winner_id === m.side_a ? "text-pb-red" : "text-zinc-300"}`}>{nameOf(m.side_a)}</span>
                  <span className="font-mono font-black text-2xl text-white tabular-nums">{m.score_a}–{m.score_b}</span>
                  <span className={`truncate flex-1 text-right ${m.winner_id === m.side_b ? "text-pb-red" : "text-zinc-300"}`}>{nameOf(m.side_b)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <SponsorSlide sponsors={t.sponsors} />
      <div className="px-8 py-2 border-t border-pb-border/60 text-[10px] uppercase tracking-[0.35em] text-zinc-600 flex justify-between">
        <span>Pickle Bullpen · Performance OS</span><span>Auto-refreshing · PSI updates the moment a score is approved</span>
      </div>
    </div>
  );
}

export default function TdCourtBoard() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tv = params.get("tv") === "1";
  const [data, setData] = useState(null);
  const [movers, setMovers] = useState(null);
  const [err, setErr] = useState(null);

  const enterTv = () => {
    setParams({ tv: "1" });
    document.documentElement.requestFullscreen?.().catch(() => {});
  };
  const exitTv = useCallback(() => {
    setParams({});
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
  }, [setParams]);
  useEffect(() => {
    if (!tv) return;
    const onKey = (e) => { if (e.key === "Escape") exitTv(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tv, exitTv]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/tournaments/${id}`);
      setData(r.data);
      api.get(`/tournaments/${id}/psi-movers?tz=${TZ}`).then((m) => setMovers(m.data)).catch(() => {});
    } catch (e) {
      setErr(e?.response?.status === 403 ? "Directors only" : "Could not load tournament");
    }
  }, [id]);
  const startDay = async () => {
    try {
      const { data: d } = await api.post(`/tournaments/${id}/psi-movers/start-day?tz=${TZ}`);
      toast.success(d.snapshotted > 0 ? `Day ${d.day} started — ${d.snapshotted} starting PSI values locked in` : `Day ${d.day} already locked in for everyone on the roster`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start the day"); }
  };
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load, 4000);

  if (err) return <div className="min-h-screen bg-black text-red-400 flex items-center justify-center font-display uppercase tracking-widest">{err}</div>;
  if (!data) return <div className="min-h-screen bg-black text-zinc-600 flex items-center justify-center font-display uppercase tracking-widest">Loading…</div>;
  const { tournament: t, matches, entries, can_manage } = data;
  if (!can_manage) return <div className="min-h-screen bg-black text-red-400 flex items-center justify-center font-display uppercase tracking-widest">Directors only</div>;

  const nameOf = (eid) => {
    if (eid === "BYE") return "BYE";
    const e = (entries || []).find((x) => x.id === eid);
    return e ? e.display_name : "TBD";
  };
  const divLabel = (m) => [m.event_type, m.age_bracket, m.division && m.division !== "All" ? m.division : null].filter(Boolean).join(" · ");

  const live = (matches || [])
    .filter((m) => m.status !== "complete" && m.court && m.side_a && m.side_b && m.side_a !== "BYE" && m.side_b !== "BYE")
    .sort((x, y) => (x.court || 99) - (y.court || 99));
  const approved = (matches || [])
    .filter((m) => m.status === "complete" && m.score_a != null)
    .sort((x, y) => (y.decided_at || "").localeCompare(x.decided_at || ""));

  if (tv) return <TvBoard t={t} live={live} approved={approved} movers={movers} nameOf={nameOf} divLabel={divLabel} onExit={exitTv} />;

  return (
    <div className="min-h-screen bg-black text-white p-4 sm:p-6" data-testid="td-court-board">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Link to={`/tournaments/${id}`} className="text-zinc-500 hover:text-pb-cyan flex items-center gap-1 text-xs uppercase tracking-widest font-bold">
          <ArrowLeft className="w-4 h-4" /> Bracket
        </Link>
        <h1 className="font-display font-black uppercase tracking-widest text-lg sm:text-xl text-pb-cyan flex items-center gap-2">
          <LayoutGrid className="w-5 h-5" /> TD Court Board — {t.name}
        </h1>
        <span className="text-[10px] text-zinc-600 uppercase tracking-widest ml-auto">auto-refreshes every 10s</span>
        <WatchLiveButton />
        <button data-testid="tdcb-tv-mode" onClick={enterTv}
          className="flex items-center gap-1.5 border border-pb-cyan/60 text-pb-cyan hover:bg-pb-cyan hover:text-black px-3 py-2 text-[10px] font-black uppercase tracking-widest transition-colors">
          <Tv className="w-4 h-4" /> TV Mode
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">Live Courts — enter scores</div>
          {live.length === 0 && <div className="border border-pb-border text-zinc-600 text-sm p-6 text-center">No matches on court right now.</div>}
          <div className="grid sm:grid-cols-2 gap-3">
            {live.map((m) => (
              <div key={m.key} className="border border-pb-cyan/40 bg-pb-cyan/5 p-3" data-testid={`tdcb-court-${m.court}`}>
                <div className="flex items-center justify-between">
                  <span className="font-display font-black text-pb-cyan text-2xl">COURT {m.court}</span>
                  <span className="flex items-center gap-2">
                    <CourtTimer since={m.court_assigned_at} testid={`tdcb-timer-${m.court}`} />
                    <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500">{divLabel(m)}</span>
                  </span>
                </div>
                <div className="mt-3 text-sm font-bold">
                  <ServeBox serving={m.first_serve === "a"} testid={`tdcb-side-a-${m.court}`}><div className="truncate">{nameOf(m.side_a)}</div></ServeBox>
                  <div className="text-[9px] text-zinc-600 uppercase tracking-widest my-1">vs</div>
                  <ServeBox serving={m.first_serve === "b"} testid={`tdcb-side-b-${m.court}`}><div className="truncate">{nameOf(m.side_b)}</div></ServeBox>
                </div>
                <ScoreEntry id={id} m={m} onSaved={load} />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4 h-fit lg:sticky lg:top-4">
        <div>
          <PsiMoversBoard data={movers} live={!movers?.final} compact testid="tdcb-psi-movers" />
          {t.status !== "complete" && (
            <button data-testid="tdcb-start-day" onClick={startDay}
              className="mt-1.5 w-full text-[9px] uppercase tracking-widest text-zinc-500 hover:text-amber-300 transition-colors text-left">
              ↻ Lock today's starting PSI for the whole roster ({(movers?.movers || []).length} tracked)
            </button>
          )}
        </div>
        <div className="border border-pb-border bg-zinc-950 p-3" data-testid="tdcb-approved-window">
          <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-pb-red mb-2">
            <CheckCircle2 className="w-4 h-4" /> Approved Scores ({approved.length})
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-pb-border/50">
            {approved.length === 0 && <div className="text-zinc-600 text-xs py-4 text-center">No approved scores yet.</div>}
            {approved.map((m) => (
              <div key={m.key} className="py-2" data-testid={`tdcb-approved-${m.key}`}>
                <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500">{divLabel(m)}</div>
                <div className="text-sm font-bold flex items-center gap-2 flex-wrap">
                  <span className={m.winner_id === m.side_a ? "text-pb-red" : ""}>{nameOf(m.side_a)}</span>
                  <span className="font-mono font-black text-white">{m.score_a}–{m.score_b}</span>
                  <span className={m.winner_id === m.side_b ? "text-pb-red" : ""}>{nameOf(m.side_b)}</span>
                </div>
                {m.court && <div className="text-[9px] uppercase tracking-widest text-pb-cyan mt-0.5">Court {m.court}</div>}
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
